import React, { useEffect, useMemo } from "react";
import Select, { components } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchUnit } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";
import { FiPlus } from "react-icons/fi";

const UnitDropdown = ({
  errors,
  control,
  setValue,
  getValues,
  watch,
  rowData,
  required,
  addUnit = false,
  onAddUnitClick,
}) => {
  const unitName = getValues().unit;
  const selectedUnit = watch("unit_id");

  const {
    data: units,
    isLoading,
    error,
  } = useQuery(["unit"], fetchUnit, {
    keepPreviousData: false,
    staleTime: DROPDOWN_STALE_TIME,
  });

  const options = useMemo(() => {
    const unitOptions =
      units?.data?.map((unit) => ({
        value: unit.id,
        label: `${unit.unit_code} / ${unit.unit_name}`,
      })) || [];

    if (addUnit) {
      return [
        {
          value: "add_unit",
          label: "➕ Add Unit",
          isAddOption: true,
        },
        ...unitOptions,
      ];
    }

    return unitOptions;
  }, [units, addUnit]);

  useEffect(() => {
    if (rowData && options.length) {
      const selectedOption = options.find(
        (option) => option.value === rowData?.unit_id
      );
      setValue("unit_id", selectedOption?.value);
    }
  }, [rowData, options, setValue]);

  const selectedOption = options.find(
    (option) => option.value === selectedUnit || unitName
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading Units</div>;

  // 👇 Custom “Add Unit” option
  const CustomOption = (props) => {
    if (props.data.isAddOption) {
      return (
        <div
          onClick={() => onAddUnitClick?.()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            cursor: "pointer",
            color: "#1677ff",
            fontWeight: 500,
          }}
        >
          <FiPlus size={16} />
          <span>Add Unit</span>
        </div>
      );
    }
    return <components.Option {...props} />;
  };

  return (
    <Controller
      name="unit_id"
      control={control}
      rules={{
        required: required ? "Unit is required" : false,
        validate: (value) => {
          if (required && !value) return "Unit is required";
          return true;
        },
      }}
      render={({ field }) => (
        <Select
          {...field}
          options={options}
          components={{ Option: CustomOption }}
          isClearable
          value={selectedOption || null}
          placeholder="Select Unit"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          onChange={(selectedOption) => {
            if (selectedOption?.isAddOption) {
              onAddUnitClick?.();
              return;
            }
            const value = selectedOption ? selectedOption.value : "";
            field.onChange(value);
            setValue("unit_id", value);
          }}
          className={`react-select-container ${
            errors?.unit_id ? "is-invalid" : ""
          }`}
        />
      )}
    />
  );
};

export default UnitDropdown;
